/**
 * Channels State
 * 
 * Contains all state for the channels subdomain:
 * - Sales channels data (from subscriptions)
 * - Channel detail view state
 * - Add channel modal state
 * - Delete confirmation state
 * - OAuth redirect handling
 */

import { FieldConfig } from '@components/application/DynamicForm/types';
import { BillingEntity } from '@appTypes/BillingEntities';
import {
  SystemIntegrationsReadmodel,
  WarehousesReadmodelType,
  PriceListReadmodelType,
  ChannelCategorySuggestionType,
  ChannelCategoryMappingConfidenceType,
  ChannelCategoryMappingSourceType,
  MercadolibreCategorySearchHitType,
  OrderStatusesReadmodelType
} from '@bett3r-dev/teselly-domain';

/**
 * A single channel category row, projected from the
 * `systems_integrations_channel_categories_readmodel` subscription. `isMapped`
 * / `mappingSource` reflect the current mapping; `suggestions` are stable
 * vector pre-fills kept through map/unmap (the UI hides them while mapped).
 */
export interface ChannelCategoryRow {
  externalId: string;
  name: string;
  path: string;
  tesellyCategoryId: string | null;
  tesellyCategoryName: string | null;
  /**
   * Full `Root > … > Leaf` breadcrumb of the MAPPED Teselly category, so two
   * same-named leaves in different branches ("Fundas" under Celulares vs under
   * Notebooks) are distinguishable at a glance — the leaf name alone is not an
   * identity (TV1-2113, S7b).
   *
   * `null` when the path is not derivable: the channel-categories readmodel
   * stores only `tesellyCategoryId` / `tesellyCategoryName` for a committed
   * mapping, so the policy recovers the breadcrumb from the row's own
   * `suggestions` when the mapped id is one of them. A category mapped by hand
   * through the free-text picker has no suggestion to recover it from and
   * renders the leaf name, as before.
   */
  tesellyCategoryPath: string | null;
  isMapped: boolean;
  mappingSource: ChannelCategoryMappingSourceType | null;
  /**
   * The model's self-reported confidence in an `ai` mapping — a coarse enum,
   * never a float. `null` for every other source.
   */
  mappingConfidence: ChannelCategoryMappingConfidenceType | null;
  /** Why the model picked this category. `null` for every other source. */
  mappingRationale: string | null;
  /**
   * The channel category is a merchandising collection ("Ofertas", "Novedades",
   * "Liquidación") with no correct Teselly category (TV1-2113, D6).
   *
   * NOT the same as `!isMapped`: unmapped means "nobody decided yet", unmappable
   * means "we decided, and the answer is that there is no answer". The UI must
   * therefore stop counting it as pending work — the whole point of recording
   * the verdict is to stop asking — while still letting the seller overrule it.
   */
  isUnmappable: boolean;
  suggestions: ChannelCategorySuggestionType[];
}

/** How the category mapper list is sorted in the UI. */
export type ChannelCategoriesListSort = 'name-asc' | 'name-desc';

/** Mapping-status buckets for the list filter (multi-select). */
export type ChannelCategoriesMappingFilter = 'unmapped' | 'mapped' | 'auto-mapped';

/** Rows per page in the category mapper (client-side; full catalog is subscribed). */
export const CATEGORIES_LIST_PAGE_SIZE = 25;

export type ChannelCategoriesListView = {
  /** 1-based current page. */
  page: number;
  sort: ChannelCategoriesListSort;
  /** Free-text filter over channel category name + path. */
  searchQuery: string;
  /** Empty = show all mapping statuses (incl. unmappable commercial collections). */
  selectedMappingStatuses: ChannelCategoriesMappingFilter[];
};

export const createInitialCategoriesListView = (): ChannelCategoriesListView => ({
  page: 1,
  sort: 'name-asc',
  searchQuery: '',
  selectedMappingStatuses: []
});

const compareCategoryNames = ( a: ChannelCategoryRow, b: ChannelCategoryRow ): number =>
  a.name.localeCompare( b.name, undefined, { sensitivity: 'base' });

export const categoryMappingFilterBucket = (
  category: ChannelCategoryRow
): ChannelCategoriesMappingFilter | 'unmappable' => {
  if ( category.isUnmappable ) return 'unmappable';
  if ( !category.isMapped ) return 'unmapped';
  if ( category.mappingSource === 'exact-match' ) return 'auto-mapped';
  return 'mapped';
};

export const filterChannelCategories = (
  categories: ChannelCategoryRow[],
  listView: ChannelCategoriesListView
): ChannelCategoryRow[] => {
  const query = listView.searchQuery.trim().toLowerCase();
  return categories.filter(( category ) => {
    if ( query ) {
      const haystack = `${ category.name } ${ category.path }`.toLowerCase();
      if ( !haystack.includes( query )) return false;
    }
    if ( listView.selectedMappingStatuses.length === 0 ) return true;
    const bucket = categoryMappingFilterBucket( category );
    return bucket !== 'unmappable' && listView.selectedMappingStatuses.includes( bucket );
  });
};

export const clampCategoriesListPage = (
  listView: ChannelCategoriesListView,
  filteredCount: number
): ChannelCategoriesListView => {
  const totalPages = Math.max( 1, Math.ceil( filteredCount / CATEGORIES_LIST_PAGE_SIZE ));
  return {
    ...listView,
    page: Math.min( Math.max( 1, listView.page ), totalPages )
  };
};

const sortChannelCategories = (
  categories: ChannelCategoryRow[],
  sort: ChannelCategoriesListSort
): ChannelCategoryRow[] => {
  const sorted = [...categories];
  sorted.sort(( a, b ) => {
    const nameOrder = compareCategoryNames( a, b );
    return sort === 'name-desc' ? -nameOrder : nameOrder;
  });
  return sorted;
};

export const buildChannelCategoriesList = (
  categories: ChannelCategoryRow[],
  listView: ChannelCategoriesListView
): ChannelCategoryRow[] => {
  const filtered = filterChannelCategories( categories, listView );
  return sortChannelCategories( filtered, listView.sort );
};

/**
 * Channel-category search state — the searchable ML-category picker.
 */
export interface ChannelCategorySearch {
  // The channel category (externalId) whose picker currently owns the search.
  // The search query/results are a SINGLE shared slice (only one picker is open
  // at a time), so this scopes the input value + results to the active row —
  // without it, every row's searchbox renders the same shared `query`.
  activeExternalId: string | null;
  query: string;
  results: MercadolibreCategorySearchHitType[];
  offset: number;
  hasMore: boolean;
  pendingLoadMore: boolean;
}

/**
 * A pending backend write for a category mapping, queued by the `user`-sourced
 * `categories/map` / `categories/unmap` handlers and drained by `categoriesPolicy`.
 *
 * This is the load-bearing distinction between **user intent** and **API
 * reconciliation**: CDSE policies' `onStateChange` fires for *every* state
 * change (incl. the `api`-sourced subscription snapshot) and is NOT told the
 * command source, so the policy MUST NOT infer "the user mapped X" from a
 * `prev`/`next` row diff — an `api` `categories/set` of already-mapped rows
 * (e.g. exact-match auto-maps from provisioning) is indistinguishable from a
 * fresh user edit and would otherwise re-dispatch `MapChannelCategory`,
 * silently overwriting the `exact-match` provenance with `manual`. Only intents
 * the user-sourced handler enqueued here are ever pushed to the backend.
 */
export type CategoryWriteIntent =
  | { kind: 'map'; externalId: string; tesellyCategoryId: string; tesellyCategoryName: string }
  | { kind: 'unmap'; externalId: string };

/**
 * Stable identity of a pending intent. The policy uses it to dedupe in-flight
 * writes and to clear only the exact intent it drained (a newer intent for the
 * same `externalId` has a different key and so survives a concurrent drain).
 */
export const categoryIntentKey = ( intent: CategoryWriteIntent ): string =>
  intent.kind === 'map'
    ? `map:${intent.externalId}:${intent.tesellyCategoryId}`
    : `unmap:${intent.externalId}`;

/**
 * A single channel→canonical order-status mapping row.
 * `channelStatus` keys are free-form text; `canonicalStatusId` references a
 * canonical OrderStatus id from `backend.queries.SalesOrderStatuses()` (WS-3).
 */
export interface StatusMappingRow {
  channelStatus: string;
  canonicalStatusId: string;
}

/**
 * RetailStore channel settings stored in channel.settings
 */
export interface RetailStoreSettings {
  warehouses: string[];
  defaultChannelWarehouseId: string | null;
  priceLists: string[];
  defaultPriceListId: string | null;
}

/**
 * Form data for adding a new channel
 */
export interface AddChannelFormData {
  storeUrl: string;
  name: string;
  consumerKey: string;
  consumerSecret: string;
  sellerId: string;
  refreshToken: string;
}

/**
 * Channel default values form data
 * Dynamic structure based on connector type
 */
export interface ChannelFormData {
  [key: string]: any;
}

/**
 * The two things a connect action can arm an automatic import of (TV1-2379).
 *
 * - `publications` — the channel's current listing catalog. Additive and
 *   re-runnable, so it is offered ticked.
 * - `orders` — the channel's FULL past order history. Heavy and effectively
 *   one-way, so it is offered UNticked, and only for connectors that can count
 *   what they are about to pull (`canImportOrderHistory`).
 */
export type ChannelImportKind = 'publications' | 'orders';

/** One connector's parked import intent. Both kinds are independent. */
export type ChannelImportIntent = {
  publications: boolean;
  orders: boolean;
};

/** An intent with nothing armed — the shape a fresh entry starts from. */
export const emptyChannelImportIntent = (): ChannelImportIntent => ({
  publications: false,
  orders: false
});

export interface ChannelsState {
  /** Sales channels data */
  salesChannels: Record<string, SystemIntegrationsReadmodel>;

  /**
   * What the operator asked to be imported automatically while connecting,
   * keyed by connector id (TV1-2354, widened for order history in TV1-2379).
   *
   * The intent has to outlive the connect action because for two of the three
   * connect paths the integration does not exist yet when the modal closes:
   * OAuth hands off to an external tab and the callback provisions server-side,
   * and the api-key path has the server provision after validating credentials.
   * Only direct-provision knows the id synchronously.
   *
   * So the choice is parked here and `channelAutoImportPolicy` fires the imports
   * when the channel actually shows up in the `ExternalSystemsIntegrations`
   * subscription — one mechanism that works for all three.
   *
   * ── Why a record keyed by connector, not two parallel id lists ────────────
   *
   * The connector id IS the match key the policy uses when a channel arrives,
   * so keying by it turns the policy's scan into a lookup and — more to the
   * point — keeps ONE entry per connector. The two kinds are independent
   * booleans INSIDE that entry, which is what preserves the one-shot semantics
   * documented on `channelAutoImportPolicy`: the first channel to arrive for a
   * connector consumes the whole entry, both kinds at once, exactly as the
   * single-kind version consumed the single id. Two parallel lists would let
   * the two kinds drift out of step — one consumed, one still armed — and
   * silently re-import against the second channel of the same connector.
   *
   * Session-scoped by design: nothing is persisted, and a page reload before the
   * OAuth callback returns simply means no auto-import. The manual import action
   * in the channel detail covers that.
   */
  pendingChannelImports: Record<string, ChannelImportIntent>;

  /**
   * Per-channel figures the LIST needs but the integrations readmodel does not
   * carry (TV1-2361), keyed by integration id.
   *
   * Loaded by `channelStatsPolicy`. Both figures are cheap for different reasons:
   * the channel-categories route takes an OPTIONAL `integrationId`, so one call
   * returns every channel's categories for the account in a single trip; the
   * publication counts are one `limit: 1` count query per channel, run in
   * parallel, which is acceptable because an account has a handful of channels
   * rather than hundreds.
   *
   * `publications: null` means "not loaded yet", distinct from `0` meaning "this
   * channel genuinely has none" — the card must not render a confident 0 while
   * the query is still in flight.
   */
  channelStats: Record<string, {
    mappedCategories: number;
    totalCategories: number;
    publications: number | null;
    /**
     * Per-concern setup state (TV1-2367), derived from what is actually
     * configured — the channel's own rules and its billing entity — not from the
     * `channelSetupCompletedAt` marker, which the old wizard wrote on exit
     * whether or not anything had been set up.
     *
     * `undefined` means NOT LOADED YET, distinct from all-false meaning "nothing
     * is configured": the card must not flash "sin configurar" on first paint.
     */
    setup?: { inventory: boolean; billing: boolean; logistics: boolean };
  }>;

  /** Channel detail view */
  channelDetail: {
    mode: { type: 'closed' } | { type: 'view'; integrationId: string };
    activeTab: 'general' | 'rules' | 'categories' | 'statusMapping';

    // Category mapping (test-channel category mapper)
    categories: ChannelCategoryRow[];
    /** Client-side list controls (sort + pagination over the subscribed catalog). */
    categoriesListView: ChannelCategoriesListView;
    categorySearch: ChannelCategorySearch;
    // Queue of user-initiated map/unmap writes awaiting backend dispatch. The
    // policy drains this; `api` reconciliation never touches it (see
    // `CategoryWriteIntent`).
    pendingCategoryWrites: CategoryWriteIntent[];
    // Pending-intent flag for a user-requested "Resync from channel". Set by the
    // user-sourced `categories/resync` handler, drained by the policy (dispatch
    // the backend resync command, then clear). `api` reconciliation never sets
    // it — same source-blind-policy discipline as `pendingCategoryWrites`.
    pendingCategoryResync: boolean;
    // Id of the `map-channel-categories` operation run for THIS integration that
    // is sitting in `ready` — computed and awaiting human review — or `null`.
    //
    // It has its own slice rather than riding the navbar batch-processes
    // dropdown because that dropdown shows work that is RUNNING, and a run
    // awaiting review is precisely NOT running: it has finished computing and is
    // blocked on the seller. Without a banner here the proposal is invisible
    // until someone happens to open the operations history.
    aiMappingReadyRunId: string | null;

    // Channel→canonical order-status mapping (WS-3)
    statusMapping: {
      rows: StatusMappingRow[];
      canonicalStatuses: OrderStatusesReadmodelType[];
      isLoading: boolean;
      isSaving: boolean;
    };

    // Billing entity editing
    billingEntityEdit: {
      isEditing: boolean;
      tempValue: string | null;
    };
    
    // Default values form
    defaultValues: {
      fieldConfig: FieldConfig[];
      data: ChannelFormData;
      originalData: ChannelFormData;
    };
    
    // RetailStore config editing (independent per section)
    retailStoreConfigEdit: {
      isEditingWarehouses: boolean;
      isEditingPriceLists: boolean;
      tempWarehouseIds: string[];
      tempDefaultChannelWarehouseId: string | null;
      tempPriceListIds: string[];
      tempDefaultPriceListId: string | null;
    };

    // External data (from backend queries)
    billingEntities: BillingEntity[];
    availableWarehouses: WarehousesReadmodelType[];
    availablePriceLists: PriceListReadmodelType[];

    // Loading states
    loading: {
      billingEntities: boolean;
      defaultValues: boolean;
      savingBilling: boolean;
      savingDefaults: boolean;
      importing: boolean;
      loadingWarehouses: boolean;
      loadingPriceLists: boolean;
      savingRetailStoreConfig: boolean;
      categoriesLoading: boolean;
      categorySearchLoading: boolean;
      categorySearchLoadingMore: boolean;
      categoriesResyncing: boolean;
    };

    // UI feedback
    saveSuccess: boolean;
  };

  /** Add channel modal */
  addChannelModal: {
    isOpen: boolean;
    step: 'selectType' | 'enterDetails' | 'connecting';
    selectedType: string | null;
    formData: AddChannelFormData;
    formErrors: Record<string, string>;
  };

  /** Delete confirmation modal */
  deleteModal: {
    isOpen: boolean;
    integrationId: string | null;
  };

  /** OAuth redirect handling */
  oauthRedirect: {
    isProcessing: boolean;
    code: string | null;
    state: string | null;
  };

  /** Loading states */
  loading: {
    channelsLoading: boolean;
    submitting: boolean;
    deleting: boolean;
  };
}

const createInitialFormData = (): AddChannelFormData => ({
  storeUrl: '',
  name: '',
  consumerKey: '',
  consumerSecret: '',
  sellerId: '',
  refreshToken: ''
});

const createInitialDefaultValues = () => ({
  fieldConfig: [],
  data: {},
  originalData: {}
});

export const initialState: ChannelsState = {
  salesChannels: {},
  pendingChannelImports: {},
  channelStats: {},

  channelDetail: {
    mode: { type: 'closed' },
    activeTab: 'general',
    categories: [],
    categoriesListView: createInitialCategoriesListView(),
    categorySearch: { activeExternalId: null, query: '', results: [], offset: 0, hasMore: false, pendingLoadMore: false },
    pendingCategoryWrites: [],
    pendingCategoryResync: false,
    aiMappingReadyRunId: null,
    statusMapping: {
      rows: [],
      canonicalStatuses: [],
      isLoading: false,
      isSaving: false
    },
    billingEntityEdit: {
      isEditing: false,
      tempValue: null
    },
    retailStoreConfigEdit: {
      isEditingWarehouses: false,
      isEditingPriceLists: false,
      tempWarehouseIds: [],
      tempDefaultChannelWarehouseId: null,
      tempPriceListIds: [],
      tempDefaultPriceListId: null
    },
    defaultValues: createInitialDefaultValues(),
    billingEntities: [],
    availableWarehouses: [],
    availablePriceLists: [],
    loading: {
      billingEntities: false,
      defaultValues: false,
      savingBilling: false,
      savingDefaults: false,
      importing: false,
      loadingWarehouses: false,
      loadingPriceLists: false,
      savingRetailStoreConfig: false,
      categoriesLoading: false,
      categorySearchLoading: false,
      categorySearchLoadingMore: false,
      categoriesResyncing: false
    },
    saveSuccess: false
  },

  addChannelModal: {
    isOpen: false,
    step: 'selectType',
    selectedType: null,
    formData: createInitialFormData(),
    formErrors: {}
  },

  deleteModal: {
    isOpen: false,
    integrationId: null
  },

  oauthRedirect: {
    isProcessing: false,
    code: null,
    state: null
  },

  loading: {
    channelsLoading: false,
    submitting: false,
    deleting: false
  }
};

export { createInitialFormData, createInitialDefaultValues };

/**
 * Selectors
 */
export const selectors = {
  getChannel: ( state: ChannelsState, integrationId: string ) =>
    state.salesChannels[integrationId],

  channelsList: ( state: ChannelsState ) =>
    Object.values( state.salesChannels ),

  currentChannel: ( state: ChannelsState ) => {
    if ( state.channelDetail.mode.type === 'closed' ) return null;
    return state.salesChannels[state.channelDetail.mode.integrationId];
  },

  isAddModalOpen: ( state: ChannelsState ) =>
    state.addChannelModal.isOpen,

  isDeleteModalOpen: ( state: ChannelsState ) =>
    state.deleteModal.isOpen,

  isSubmitting: ( state: ChannelsState ) =>
    state.loading.submitting,
  
  // Detail view selectors
  channelDetailMode: ( state: ChannelsState ) => 
    state.channelDetail.mode,
  
  activeTab: ( state: ChannelsState ) => 
    state.channelDetail.activeTab,
  
  billingEntities: ( state: ChannelsState ) => 
    state.channelDetail.billingEntities,
  
  isBillingEntityEditing: ( state: ChannelsState ) => 
    state.channelDetail.billingEntityEdit.isEditing,
  
  billingEntityTempValue: ( state: ChannelsState ) => 
    state.channelDetail.billingEntityEdit.tempValue,
  
  defaultValues: ( state: ChannelsState ) => 
    state.channelDetail.defaultValues,
  
  hasUnsavedDefaultValues: ( state: ChannelsState ) => 
    JSON.stringify( state.channelDetail.defaultValues.data ) !== 
    JSON.stringify( state.channelDetail.defaultValues.originalData ),
  
  detailLoading: ( state: ChannelsState ) =>
    state.channelDetail.loading,

  saveSuccess: ( state: ChannelsState ) =>
    state.channelDetail.saveSuccess,

  retailStoreConfigEdit: ( state: ChannelsState ) =>
    state.channelDetail.retailStoreConfigEdit,

  isEditingWarehouses: ( state: ChannelsState ) =>
    state.channelDetail.retailStoreConfigEdit.isEditingWarehouses,

  isEditingPriceLists: ( state: ChannelsState ) =>
    state.channelDetail.retailStoreConfigEdit.isEditingPriceLists,

  availableWarehouses: ( state: ChannelsState ) =>
    state.channelDetail.availableWarehouses,

  availablePriceLists: ( state: ChannelsState ) =>
    state.channelDetail.availablePriceLists,

  // Category mapping selectors
  selectChannelCategories: ( state: ChannelsState ): ChannelCategoryRow[] =>
    state.channelDetail.categories,

  selectCategoriesListSort: ( state: ChannelsState ): ChannelCategoriesListSort =>
    state.channelDetail.categoriesListView.sort,

  selectCategoriesListSearchQuery: ( state: ChannelsState ): string =>
    state.channelDetail.categoriesListView.searchQuery,

  selectCategoriesListMappingFilters: (
    state: ChannelsState
  ): ChannelCategoriesMappingFilter[] =>
    state.channelDetail.categoriesListView.selectedMappingStatuses,

  selectCategoriesListPage: ( state: ChannelsState ): number =>
    state.channelDetail.categoriesListView.page,

  selectFilteredChannelCategoriesCount: ( state: ChannelsState ): number =>
    filterChannelCategories(
      state.channelDetail.categories,
      state.channelDetail.categoriesListView
    ).length,

  selectCategoriesListTotalPages: ( state: ChannelsState ): number =>
    Math.max(
      1,
      Math.ceil(
        filterChannelCategories(
          state.channelDetail.categories,
          state.channelDetail.categoriesListView
        ).length / CATEGORIES_LIST_PAGE_SIZE
      )
    ),

  selectSortedChannelCategories: ( state: ChannelsState ): ChannelCategoryRow[] =>
    buildChannelCategoriesList(
      state.channelDetail.categories,
      state.channelDetail.categoriesListView
    ),

  selectPaginatedChannelCategories: ( state: ChannelsState ): ChannelCategoryRow[] => {
    const sorted = buildChannelCategoriesList(
      state.channelDetail.categories,
      state.channelDetail.categoriesListView
    );
    const page = state.channelDetail.categoriesListView.page;
    const start = ( page - 1 ) * CATEGORIES_LIST_PAGE_SIZE;
    return sorted.slice( start, start + CATEGORIES_LIST_PAGE_SIZE );
  },

  selectIsCategoryMapped: ( state: ChannelsState, externalId: string ): boolean => {
    const category = state.channelDetail.categories.find(
      ( c ) => c.externalId === externalId
    );
    return category?.isMapped ?? false;
  },

  /**
   * Deliberately still `exact-match` ONLY. An `ai` mapping is a different kind
   * of claim — a model proposed it and a human approved it in the operations
   * review grid — and the tab renders the two with different badges, so
   * collapsing them here would erase exactly the distinction the seller needs.
   * See `selectIsCategoryAiMapped`.
   */
  selectIsCategoryAutoMapped: ( state: ChannelsState, externalId: string ): boolean => {
    const category = state.channelDetail.categories.find(
      ( c ) => c.externalId === externalId
    );
    return category?.mappingSource === 'exact-match';
  },

  /** Mapped by the `map-channel-categories` operation (model-proposed, human-approved). */
  selectIsCategoryAiMapped: ( state: ChannelsState, externalId: string ): boolean => {
    const category = state.channelDetail.categories.find(
      ( c ) => c.externalId === externalId
    );
    return category?.mappingSource === 'ai';
  },

  /** The `ai` mapping's audit trail — confidence + rationale, `null` otherwise. */
  selectCategoryMappingConfidence: (
    state: ChannelsState,
    externalId: string
  ): ChannelCategoryMappingConfidenceType | null => {
    const category = state.channelDetail.categories.find(
      ( c ) => c.externalId === externalId
    );
    return category?.mappingConfidence ?? null;
  },

  selectCategoryMappingRationale: (
    state: ChannelsState,
    externalId: string
  ): string | null => {
    const category = state.channelDetail.categories.find(
      ( c ) => c.externalId === externalId
    );
    return category?.mappingRationale ?? null;
  },

  /** Full breadcrumb of the mapped Teselly category when known (see the row doc). */
  selectCategoryMappedPath: (
    state: ChannelsState,
    externalId: string
  ): string | null => {
    const category = state.channelDetail.categories.find(
      ( c ) => c.externalId === externalId
    );
    if ( !category ) return null;
    return category.tesellyCategoryPath ?? category.tesellyCategoryName ?? null;
  },

  /** Recorded as a merchandising collection with no correct Teselly category (D6). */
  selectIsCategoryUnmappable: ( state: ChannelsState, externalId: string ): boolean => {
    const category = state.channelDetail.categories.find(
      ( c ) => c.externalId === externalId
    );
    return category?.isUnmappable ?? false;
  },

  /**
   * How many channel categories still need a decision.
   *
   * Unmappable rows are EXCLUDED: they have already been decided, and counting
   * them would keep the tab reporting outstanding work forever — the exact nag
   * that recording the verdict exists to stop (D6). Mapped rows are excluded for
   * the obvious reason.
   */
  selectPendingCategoriesCount: ( state: ChannelsState ): number =>
    state.channelDetail.categories.filter(
      ( c ) => !c.isMapped && !c.isUnmappable
    ).length,

  /**
   * The `map-channel-categories` run for this integration awaiting review, or
   * `null`. Drives the "your AI mapping is ready" banner.
   */
  selectAiMappingReadyRunId: ( state: ChannelsState ): string | null =>
    state.channelDetail.aiMappingReadyRunId,

  selectCategorySuggestions: (
    state: ChannelsState,
    externalId: string
  ): ChannelCategorySuggestionType[] => {
    const category = state.channelDetail.categories.find(
      ( c ) => c.externalId === externalId
    );
    return category?.suggestions ?? [];
  },

  selectCategorySearchResults: (
    state: ChannelsState
  ): MercadolibreCategorySearchHitType[] =>
    state.channelDetail.categorySearch.results,

  selectCategorySearchQuery: ( state: ChannelsState ): string =>
    state.channelDetail.categorySearch.query,

  selectCategorySearchActiveExternalId: ( state: ChannelsState ): string | null =>
    state.channelDetail.categorySearch.activeExternalId,

  selectCategoriesLoading: ( state: ChannelsState ): boolean =>
    state.channelDetail.loading.categoriesLoading,

  selectCategoriesResyncing: ( state: ChannelsState ): boolean =>
    state.channelDetail.loading.categoriesResyncing,

  selectCategorySearchHasMore: ( state: ChannelsState ): boolean =>
    state.channelDetail.categorySearch.hasMore,

  selectCategorySearchLoadingMore: ( state: ChannelsState ): boolean =>
    state.channelDetail.loading.categorySearchLoadingMore,

  // Status mapping selectors (WS-3)
  statusMapping: ( state: ChannelsState ) =>
    state.channelDetail.statusMapping,

  statusMappingRows: ( state: ChannelsState ) =>
    state.channelDetail.statusMapping.rows,

  canonicalStatuses: ( state: ChannelsState ) =>
    state.channelDetail.statusMapping.canonicalStatuses,

  isStatusMappingSaving: ( state: ChannelsState ) =>
    state.channelDetail.statusMapping.isSaving
};
